import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import standardBizApiClient from "../../../../util/BizApi/standardBizApiClient";
import Alert from "../../../../components/customMui/Alert";
import PracticeTestResult from "../../practiceTest/practiceTestResult/PracticeTestResult";
import getScoreColor from "../../../../util/functions/getScoreColor";
import "./practiceDomainHistory.css";

const PracticeDomainHistory = ({ domain, onBack }) => {
    const { certList, certCodeInFocus } = useSelector((state) => state.practice);
    const [ showPageLoader, setShowPageLoader] = useState(true);
    const [ certInFocus, setCertInFocus ] = useState(null);
    const [ showDetailsLoader, setShowDetailsLoader ] = useState(null);
    const [ errorMessage, setErrorMessage ] = useState(null);
    const [ examSummary, setExamSummary ] = useState(null);

    useEffect(() => {
        handleFormatCert();
    }, [certList, certCodeInFocus]);

    const handleFormatCert = () => {
        let newCertInFocus = certList.filter(cert => cert.cert.code === certCodeInFocus);
        if (newCertInFocus.length === 0) {
            setShowPageLoader(false);
            return;
        }
        newCertInFocus = newCertInFocus[0];
        setCertInFocus(newCertInFocus);
        setShowPageLoader(false);
    }
    
    const handleShowExamSummary = async (examId) => {
        setErrorMessage(null);
        setShowDetailsLoader(examId);
        const examSummaryRes = await standardBizApiClient.exam_summary(examId);
        if (examSummaryRes?.is_error) {
            setErrorMessage("Error while getting test details, please try again soon.");
            setShowDetailsLoader(null);
            return;
        }
        setExamSummary(examSummaryRes);
        setShowDetailsLoader(null);
    }

    const handleHideExamSummary = () => {
        setExamSummary(null);
    }    

    if (!domain || showPageLoader) {
        return null;
    }

    if (domain.test_history.length === 0) {
        return (
            <p>There are no completed tests for {domain?.domain?.name ? domain.domain.name : "this domain"}.</p>
        )
    }

    const testsTaken = domain.test_history.length;
    const averageScoreFactor = testsTaken > 0
        ? domain.test_history.reduce((acc, test) => acc + Number(test.score_factor || 0), 0) / testsTaken
        : 0;
    const bestScoreFactor = domain.test_history.reduce((acc, test) => Math.max(acc, Number(test.score_factor || 0)), 0);
    const averageScorePercent = Math.round(averageScoreFactor * 100);
    const bestScorePercent = Math.round(bestScoreFactor * 100);
    const scoreColor = getScoreColor(averageScorePercent);

    return (
        <div className="practice-domain-history">
            {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
            {examSummary ? (
                <PracticeTestResult examSummary={examSummary} onBack={handleHideExamSummary} />
            ) : (
                <>
                    <section className="practice-domain-history-hero">
                        <button className="practice-domain-history-back-button" onClick={onBack} aria-label="Back">
                            <ArrowBackIcon />
                        </button>
                        <div>
                            <h1>Test History</h1>
                            <p>Track your progress and review past performance</p>
                        </div>
                        <div
                            className="practice-domain-history-hero-score-card"
                            style={{
                                "--score-percent": averageScorePercent,
                                "--score-color": scoreColor
                            }}
                        >
                            <div className="practice-domain-history-hero-score-ring">
                                <div>
                                    <strong>{averageScorePercent}%</strong>
                                </div>
                            </div>
                            <div>
                                <p className="practice-domain-history-hero-score-label">
                                    <EmojiEventsIcon /> Avg. Score
                                </p>
                                <p className="practice-domain-history-hero-score-meta">
                                    {testsTaken} {testsTaken === 1 ? "test" : "tests"} taken
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="practice-domain-history-stat-grid">
                        <div className="practice-domain-history-stat-card">
                            <AssignmentTurnedInIcon />
                            <strong>{testsTaken}</strong>
                            <p>Tests Taken</p>
                        </div>
                        <div
                            className="practice-domain-history-stat-card practice-domain-history-stat-card-score"
                            style={{ "--score-color": getScoreColor(averageScorePercent) }}
                        >
                            <TrendingUpIcon />
                            <strong>{averageScorePercent}%</strong>
                            <p>Average Score</p>
                        </div>
                        <div
                            className="practice-domain-history-stat-card practice-domain-history-stat-card-score"
                            style={{ "--score-color": getScoreColor(bestScorePercent) }}
                        >
                            <EmojiEventsIcon />
                            <strong>{bestScorePercent}%</strong>
                            <p>Best Score</p>
                        </div>
                    </section>

                    <section className="practice-domain-history-card-section">
                        <div className="practice-domain-history-section-header">
                            <div>
                                <h2>Test History</h2>
                                <p>Your recent test performance</p>
                            </div>
                            <span>{testsTaken}{testsTaken === 1 ? "Test" : "Tests"}</span>
                        </div>
                        <div className="practice-domain-history-card-grid">
                            {domain.test_history.map((test, index) => {
                                const createdDt = moment.utc(test.created_dt);
                                const createdDate = createdDt.local().format("MMM D, YYYY");
                                const scorePercent = Math.round(Number(test.score_factor || 0) * 100);
                                const correctCount = Number(test.peq_answer_pass || 0);
                                const incorrectCount = Number(test.peq_answer_fail || 0);
                                const scoreColor = getScoreColor(scorePercent);

                                return (
                                    <button
                                        key={index}
                                        className="practice-domain-history-test-card"
                                        style={{ "--score-color": scoreColor }}
                                        onClick={() => handleShowExamSummary(test.practice_exam_id)}
                                        disabled={showDetailsLoader === test.practice_exam_id}
                                    >
                                        <div className="practice-domain-history-test-card-top">
                                            <div>
                                                <h3>Test {index + 1}</h3>
                                                <p><CalendarTodayIcon /> {createdDate}</p>
                                            </div>
                                            <div className="practice-domain-history-score-badge">
                                                <strong>{scorePercent}%</strong>
                                                <span>Score</span>
                                            </div>
                                        </div>
                                        <div className="practice-domain-history-count-grid">
                                            <div>
                                                <strong>{correctCount}</strong>
                                                <span>correct</span>
                                            </div>
                                            <div>
                                                <strong>{incorrectCount}</strong>
                                                <span>incorrect</span>
                                            </div>
                                        </div>
                                        <div className="practice-domain-history-card-progress">
                                            <div style={{ width: `${scorePercent}%` }} />
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                    </section>
                </>
            )}
        </div>
    )
}

export default PracticeDomainHistory;
